import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const ReviewActions = ({ transaction, onAction }) => {
  const [selectedAction, setSelectedAction] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [requestInfoType, setRequestInfoType] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const rejectionReasons = [
    { value: 'insufficient_documentation', label: 'Insufficient Documentation' },
    { value: 'suspicious_activity', label: 'Suspicious Activity Detected' },
    { value: 'sanctions_match', label: 'Sanctions List Match' },
    { value: 'pep_risk', label: 'PEP Risk Too High' },
    { value: 'source_of_funds', label: 'Unverified Source of Funds' },
    { value: 'compliance_violation', label: 'Compliance Policy Violation' },
    { value: 'other', label: 'Other (specify in notes)' }
  ];

  const requestInfoTypes = [
    { value: 'additional_documents', label: 'Additional Documents Required' },
    { value: 'source_verification', label: 'Source of Funds Verification' },
    { value: 'identity_clarification', label: 'Identity Clarification' },
    { value: 'transaction_purpose', label: 'Transaction Purpose Details' },
    { value: 'beneficiary_relationship', label: 'Beneficiary Relationship Proof' },
    { value: 'other', label: 'Other Information Required' }
  ];

  const handleApprove = async () => {
    setIsProcessing(true);
    try {
      await onAction('approve', {
        transactionId: transaction?.id,
        notes: additionalNotes,
        timestamp: new Date()?.toISOString()
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason) {
      alert('Please select a rejection reason');
      return;
    }
    
    setIsProcessing(true);
    try {
      await onAction('reject', {
        transactionId: transaction?.id,
        reason: rejectionReason,
        notes: additionalNotes,
        timestamp: new Date()?.toISOString()
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRequestInfo = async () => {
    if (!requestInfoType) {
      alert('Please select the type of information required');
      return;
    }
    
    setIsProcessing(true);
    try {
      await onAction('request_info', {
        transactionId: transaction?.id,
        infoType: requestInfoType,
        notes: additionalNotes,
        timestamp: new Date()?.toISOString()
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEscalate = async () => {
    setIsProcessing(true);
    try {
      await onAction('escalate', {
        transactionId: transaction?.id,
        notes: additionalNotes,
        timestamp: new Date()?.toISOString()
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const getRiskLevelColor = (score) => {
    if (score <= 30) return 'text-success';
    if (score <= 60) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Review Actions</h2>
        <div className="flex items-center space-x-2">
          <Icon name="Gavel" size={20} className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Decision Required</span>
        </div>
      </div>
      {/* Transaction Summary */}
      <div className="bg-muted/30 rounded-lg p-4 mb-6">
        <h3 className="font-medium text-foreground mb-3">Quick Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Amount:</span>
            <span className="text-foreground font-medium">
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: transaction.sendCurrency,
              })?.format(transaction?.sendAmount)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Risk Score:</span>
            <span className={`font-medium ${getRiskLevelColor(transaction?.riskScore)}`}>
              {transaction?.riskScore}/100
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Customer:</span>
            <span className="text-foreground">{transaction?.sender?.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Destination:</span>
            <span className="text-foreground">{transaction?.recipient?.country}</span>
          </div>
        </div>
      </div>
      {/* Action Selection */}
      <div className="space-y-6">
        <div>
          <h3 className="font-medium text-foreground mb-4">Select Action</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Button
              variant={selectedAction === 'approve' ? 'success' : 'outline'}
              onClick={() => setSelectedAction('approve')}
              iconName="CheckCircle"
              iconPosition="left"
              iconSize={16}
              className="justify-start"
            >
              Approve
            </Button>
            <Button
              variant={selectedAction === 'reject' ? 'destructive' : 'outline'}
              onClick={() => setSelectedAction('reject')}
              iconName="XCircle"
              iconPosition="left"
              iconSize={16}
              className="justify-start"
            >
              Reject
            </Button>
            <Button
              variant={selectedAction === 'request_info' ? 'warning' : 'outline'}
              onClick={() => setSelectedAction('request_info')}
              iconName="MessageCircle"
              iconPosition="left"
              iconSize={16}
              className="justify-start"
            >
              Request Info
            </Button>
            <Button
              variant={selectedAction === 'escalate' ? 'secondary' : 'outline'}
              onClick={() => setSelectedAction('escalate')}
              iconName="ArrowUp"
              iconPosition="left"
              iconSize={16}
              className="justify-start"
            >
              Escalate
            </Button>
          </div>
        </div>

        {/* Action-specific Forms */}
        {selectedAction === 'reject' && (
          <div className="space-y-4 p-4 bg-error/5 border border-error/20 rounded-lg">
            <h4 className="font-medium text-foreground flex items-center">
              <Icon name="AlertTriangle" size={16} className="mr-2 text-error" />
              Rejection Details
            </h4>
            <Select
              label="Rejection Reason"
              placeholder="Select reason for rejection"
              options={rejectionReasons}
              value={rejectionReason}
              onChange={setRejectionReason}
              required
            />
          </div>
        )}

        {selectedAction === 'request_info' && (
          <div className="space-y-4 p-4 bg-warning/5 border border-warning/20 rounded-lg">
            <h4 className="font-medium text-foreground flex items-center">
              <Icon name="Info" size={16} className="mr-2 text-warning" />
              Information Request
            </h4>
            <Select
              label="Information Type"
              placeholder="Select type of information needed"
              options={requestInfoTypes}
              value={requestInfoType}
              onChange={setRequestInfoType}
              required
            />
          </div>
        )}

        {selectedAction === 'escalate' && (
          <div className="space-y-4 p-4 bg-secondary/5 border border-secondary/20 rounded-lg">
            <h4 className="font-medium text-foreground flex items-center">
              <Icon name="AlertCircle" size={16} className="mr-2 text-secondary" />
              Escalation Notice
            </h4>
            <p className="text-sm text-muted-foreground">
              This transaction will be escalated to senior compliance officers for further review. 
              Please provide detailed notes explaining the reason for escalation.
            </p>
          </div>
        )}

        {selectedAction === 'approve' && (
          <div className="space-y-4 p-4 bg-success/5 border border-success/20 rounded-lg">
            <h4 className="font-medium text-foreground flex items-center">
              <Icon name="CheckCircle" size={16} className="mr-2 text-success" />
              Approval Confirmation
            </h4>
            <p className="text-sm text-muted-foreground">
              This transaction will be approved and proceed to processing. 
              Any additional notes will be recorded in the audit trail.
            </p>
          </div>
        )}

        {/* Additional Notes */}
        {selectedAction && (
          <div>
            <Input
              label="Additional Notes"
              type="text"
              placeholder="Enter any additional comments or observations..."
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e?.target?.value)}
              description="These notes will be recorded in the transaction audit trail"
            />
          </div>
        )}

        {/* Action Buttons */}
        {selectedAction && (
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
            {selectedAction === 'approve' && (
              <Button
                variant="success"
                onClick={handleApprove}
                loading={isProcessing}
                iconName="CheckCircle"
                iconPosition="left"
                iconSize={16}
                className="flex-1"
              >
                Confirm Approval
              </Button>
            )}
            
            {selectedAction === 'reject' && (
              <Button
                variant="destructive"
                onClick={handleReject}
                loading={isProcessing}
                iconName="XCircle"
                iconPosition="left"
                iconSize={16}
                className="flex-1"
              >
                Confirm Rejection
              </Button>
            )}
            
            {selectedAction === 'request_info' && (
              <Button
                variant="warning"
                onClick={handleRequestInfo}
                loading={isProcessing}
                iconName="MessageCircle"
                iconPosition="left"
                iconSize={16}
                className="flex-1"
              >
                Send Information Request
              </Button>
            )}
            
            {selectedAction === 'escalate' && (
              <Button
                variant="secondary"
                onClick={handleEscalate}
                loading={isProcessing}
                iconName="ArrowUp"
                iconPosition="left"
                iconSize={16}
                className="flex-1"
              >
                Escalate Transaction
              </Button>
            )}
            
            <Button
              variant="outline"
              onClick={() => {
                setSelectedAction('');
                setRejectionReason('');
                setRequestInfoType('');
                setAdditionalNotes('');
              }}
              disabled={isProcessing}
              className="flex-1 sm:flex-none"
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
      {/* Quick Actions */}
      <div className="mt-6 pt-6 border-t border-border">
        <h3 className="font-medium text-foreground mb-3">Quick Actions</h3>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="ghost"
            iconName="MessageSquare"
            iconPosition="left"
            iconSize={14}
          >
            Add Note
          </Button>
          <Button
            size="sm"
            variant="ghost"
            iconName="Download"
            iconPosition="left"
            iconSize={14}
          >
            Export Report
          </Button>
          <Button
            size="sm"
            variant="ghost"
            iconName="History"
            iconPosition="left"
            iconSize={14}
          >
            View History
          </Button>
          <Button
            size="sm"
            variant="ghost"
            iconName="Phone"
            iconPosition="left"
            iconSize={14}
          >
            Contact Customer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReviewActions;